import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Filter, Displayable } from '../../models/filter-models';
import { DisplayType, UniquesTieringMode, EndgameContent, EndgameContentIconMap, EndgameContentLabelMap } from "../../models/enums";
import { Rarity } from "../../models/enums";
import { FormsModule } from '@angular/forms';
import { ItemDataService } from '../../services/item-data.service';
import { UniqueItem, TieredUnique, UniqueTierType, UniqueCategory, UniquesTierValueList } from '../../models/item-data-models';
import { ItemLabelComponent } from '../../common/item-label/item-label.component';
import { FilterService } from '../../services/filter.service';
import { TierStyleComponent } from '../../common/tier-style/tier-style.component';
import { UniquesTierService } from '../../services/tiering/uniques-tier.service';
import { PriceTypeIndicatorComponent } from '../../common/price-type-indicator/price-type-indicator.component';
import { TooltipWrapperComponent } from '../../common/tooltip-wrapper/tooltip-wrapper.component';
import { AutocompleteComponent } from '../../common/autocomplete/autocomplete.component';
import { ChanceBasesTierService } from '../../services/tiering/chance-bases-tier.service';
import { SelectComponent, SelectOptionComponent } from '../../common/select/select.component';
import { BaseComponent } from '../../common/base.component';
import { GlobalShortcutFocusDirective } from '../../common/global-shortcut-focus.directive';
import { DynamicContentComponent } from '../../common/dynamic-content/dynamic-content.component';
import { hasMovedFromOriginalTier } from '../tier-helpers';
import { getEffectiveTierThresholdValue } from '../tier-value-helpers';
import { EconomyService } from '../../services/economy.service';
import { Economy } from '../../models/economy-models';
import { CustomUniquesTieringModeDialogComponent } from '../../custom-uniques-tiering-mode-dialog/custom-uniques-tiering-mode-dialog.component';
import { EndgameContentPresetService, BossOnlyLowValueBossUnique } from '../../services/endgame-content-preset.service';

interface UniqueItemFilterContext {
  favouriteRestrictedBaseTypes: ReadonlySet<string>;
  favouriteLowValueBaseTypes: ReadonlySet<string>;
  otherLowValueBaseTypes: ReadonlySet<string>;
  favouriteContentDisplayByBaseType: ReadonlyMap<string, { label: string; icon: string }>;
  dimNonFavouriteRestricted: boolean;
  dimNonExceptionLowValue: boolean;
}

interface TieredUniqueVm extends TieredUnique {
  previousTierColor?: string;
  movedUpArray?: number[];
  movedDownArray?: number[];
  inactiveByFilter?: boolean;
  pinnedFavouriteContentLabel?: string;
  pinnedFavouriteContentIcon?: string;
  pinnedOtherLowValueBossShown?: boolean;
}

interface TierColumnVm {
  tiers: TierDataVm[];
}

interface TierDataVm {
  tier: UniqueTierType;
  name: string;
  styleDialogTitle: string;
  specialLabel?: string;
  priceLabel?: string;
  priceBaseCurrency?: string;
  priceBaseCurrencyImage?: string;
  cosmetics: Displayable;
  exampleName?: string;
  show: boolean;
  classes: { class: string, items: TieredUniqueVm[] }[];
  isDragOver?: boolean;
  dragCounter?: number;
  tierColor?: string;
}

@Component({
  selector: 'app-unique-tiers',
  templateUrl: './unique-tiers.component.html',
  styleUrls: ['./unique-tiers.component.scss', '../tier-tab-common.scss'],
  imports: [FormsModule, ItemLabelComponent, PriceTypeIndicatorComponent, TierStyleComponent, TooltipWrapperComponent,
    AutocompleteComponent, SelectComponent, SelectOptionComponent, GlobalShortcutFocusDirective, DynamicContentComponent, CustomUniquesTieringModeDialogComponent]
})
export class UniqueTiersComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input() changedOnly = false;

  uniquesData: UniqueItem[];
  selectedTierValueList: UniquesTierValueList = [];
  goodThreshold: number = 5;
  excellentThreshold: number = 50;
  downgradeExcellentByLowestValueThreshold: number = 10;
  downgradeExcellentByAverageValueThreshold: number = 20;
  downgradeGoodThreshold: number = 2.5;
  tierCutoffValues: { mode: UniquesTieringMode, values: UniquesTierValueList }[] = [];
  tieredUniques: Record<UniqueTierType, TieredUnique[]>;
  classes: { name: string, image: string }[] = [];

  tierList: TierColumnVm[] = [];
  inflation!: number;

  draggedItem: TieredUniqueVm | null = null;

  selectedUniqueCategory?: string;
  uniqueFilter: string = '';
  filteredOtherBaseTypes: { class: string, items: TieredUniqueVm[] }[] = [];
  showCustomTieringDialog = false;

  Rarity = Rarity;
  DisplayType = DisplayType;
  uniqueCategories = Object.keys(UniqueCategory);
  Economy = Economy;

  currency = this.poe1 ? 'c' : ' ex';

  EndgameContentIconMap = EndgameContentIconMap;
  EndgameContentLabelMap = EndgameContentLabelMap;

  constructor(private itemDataService: ItemDataService, private filterService: FilterService,
    private uniquesTierService: UniquesTierService,
    private chanceBasesTierService: ChanceBasesTierService,
    private economyService: EconomyService,
    private endgameContentPresetService: EndgameContentPresetService) {
    super();
    this.uniquesData = this.itemDataService.getUniquesList();
    this.tierCutoffValues = this.uniquesTierService.getTierCutoffValues();
    this.tieredUniques = this.uniquesTierService.getTieredUniques();
    this.classes = this.itemDataService.getClasses();
    this.inflation = this.economyService.getInflation() ?? 0;

    if (this.poe1) {
      this.uniqueCategories = this.uniqueCategories.filter(c => c !== 'Maps');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['changedOnly']) {
      this.buildTierList();
    }
  }

  buildTierList(): void {
    this.uniquesTierService.combineTieredUniques(this.filter);
    this.tieredUniques = this.uniquesTierService.getTieredUniques();

    const favouriteRestrictedBaseTypes = new Set(
      this.endgameContentPresetService.getFavouriteRestrictedBossBaseTypes(this.filter, this.tieredUniques['Restricted']),
    );
    const bossOnlyLowValueBossUniques = this.endgameContentPresetService.getBossOnlyLowValueBossUniques(this.filter, this.tieredUniques['Other']);
    const favouriteLowValueBaseTypes = new Set(
      this.filter.showFavouriteLowValueBossUniques
        ? bossOnlyLowValueBossUniques.filter(x => x.isFavourite).map(x => x.baseType)
        : [],
    );
    const otherLowValueBaseTypes = new Set(
      this.filter.showOtherLowValueBossUniques
        ? bossOnlyLowValueBossUniques.filter(x => !x.isFavourite).map(x => x.baseType)
        : [],
    );
    const favouriteContentDisplayByBaseType = this.buildFavouriteContentDisplayByBaseType(
      favouriteRestrictedBaseTypes,
      favouriteLowValueBaseTypes,
      bossOnlyLowValueBossUniques,
    );

    const restrictedActive = this.filter.uniqueTierCosmetics.find(t => t.uniqueTier === 'Restricted')?.displayOptions.active ?? true;
    const otherActive = this.filter.uniqueTierCosmetics.find(t => t.uniqueTier === 'Other')?.displayOptions.active ?? true;
    const hasSpecificFavouriteEndgameContent = !this.filter.favouriteEndgameContent.includes('All');
    const hasLowValueBossExceptions =
      (this.filter.showFavouriteLowValueBossUniques && hasSpecificFavouriteEndgameContent)
      || this.filter.showOtherLowValueBossUniques;
    const itemFilterContext: UniqueItemFilterContext = {
      favouriteRestrictedBaseTypes,
      favouriteLowValueBaseTypes,
      otherLowValueBaseTypes,
      favouriteContentDisplayByBaseType,
      dimNonFavouriteRestricted: !restrictedActive
        && this.filter.showFavouriteRestrictedBossUniques
        && hasSpecificFavouriteEndgameContent,
      dimNonExceptionLowValue: this.filter.hideAllRemainingUniques
        && !otherActive
        && hasLowValueBossExceptions,
    };

    this.selectedTierValueList = this.uniquesTierService.getSelectedTierCutoffValues();
    const goodTier = this.selectedTierValueList.find(t => t.tier === 'Good');
    const excellentTier = this.selectedTierValueList.find(t => t.tier === 'Excellent');
    this.goodThreshold = goodTier ? getEffectiveTierThresholdValue(goodTier) : 5;
    this.excellentThreshold = excellentTier ? getEffectiveTierThresholdValue(excellentTier) : 50;
    this.downgradeExcellentByLowestValueThreshold = this.goodThreshold;
    this.downgradeExcellentByAverageValueThreshold = this.goodThreshold * 2;
    this.downgradeGoodThreshold = this.goodThreshold / 2;

    const tiers = Object.entries(this.tieredUniques)
      .map(([tier, items]) => {
        const uniqueTier = tier as UniqueTierType;
        const cosmetics = this.filter.uniqueTierCosmetics.find(c => c.uniqueTier === uniqueTier || (tier === 'Class' && c.uniqueTier === 'Potential'));
        const tierActive = cosmetics?.displayOptions.active ?? true;

        const { priceLabel, priceBaseCurrency, priceBaseCurrencyImage } = this.uniquesTierService.getDisplayValue(uniqueTier);

        return {
          tier,
          tierColor: this.getTierColor(uniqueTier),
          name: tier === 'Class' ? 'Class-specific' : tier,
          styleDialogTitle: (tier === 'Class' ? 'Potential' : tier) + ' Uniques',
          priceLabel,
          priceBaseCurrency,
          priceBaseCurrencyImage,
          cosmetics: cosmetics,
          exampleName: items[0]?.baseType,
          show: cosmetics === undefined ? true : cosmetics.displayOptions.hidden,
          classes: items.reduce((acc, item) => {
            const uniqueItem = this.uniquesData.find(unique => unique.name === item.allUniques[0].name)!;
            const className = item.classSpecific || uniqueItem.category || 'Default';
            if (this.selectedUniqueCategory && uniqueItem.category !== this.selectedUniqueCategory) {
              return acc;
            }
            if (this.uniqueFilter && this.uniqueFilter.length > 1 && !this.matchesUniqueFilter(item)) {
              return acc;
            }
            let itemsByClassName = acc.find(i => i.class === className);
            if (!itemsByClassName) {
              acc.push({ class: className, items: [] });
              itemsByClassName = acc.find(i => i.class === className);
            }

            const itemFilterState = this.computeItemFilterVmState(item.baseType, uniqueTier, tierActive, itemFilterContext);

            const baseTypeVm = {
              ...item,
              inactiveByFilter: itemFilterState.inactiveByFilter,
              pinnedFavouriteContentLabel: itemFilterState.pinnedFavouriteContentLabel,
              pinnedFavouriteContentIcon: itemFilterState.pinnedFavouriteContentIcon,
              pinnedOtherLowValueBossShown: itemFilterState.pinnedOtherLowValueBossShown,
            } as TieredUniqueVm;

            if (!item.originalTier && baseTypeVm.previousTier) {
              baseTypeVm.previousTierColor = this.getTierColor(baseTypeVm.previousTier);

              baseTypeVm.movedUpArray = Array.from({ length: baseTypeVm.movedUp || 0 }, (_, i) => i);
              baseTypeVm.movedDownArray = Array.from({ length: baseTypeVm.movedDown || 0 }, (_, i) => i);
            }

            itemsByClassName!.items.push(baseTypeVm);
            return acc;
          }, [] as { class: string, items: TieredUniqueVm[] }[])
          .filter(t => !!t && t.items.length > 0)
          .sort((a, b) => {
            const order = ['Weapons', 'Armour', 'Jewellery', 'Jewels', 'Flasks', 'Maps', 'Other'];
            const indexA = order.indexOf(a.class);
            const indexB = order.indexOf(b.class);

            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            } else if (indexA !== -1) {
              return -1;
            } else if (indexB !== -1) {
              return 1;
            } else {
              return a.class.localeCompare(b.class);
            }
          })
        } as TierDataVm;
      }).filter(t => !!t).map(t => this.applyChangedOnly(t));

    Object.values(tiers).forEach(tier => {
      tier.classes.forEach(group => {
        group.items.sort((a, b) => {
          const aPinned = !!a.pinnedFavouriteContentIcon || !!a.pinnedOtherLowValueBossShown;
          const bPinned = !!b.pinnedFavouriteContentIcon || !!b.pinnedOtherLowValueBossShown;
          if (aPinned !== bPinned) {
            return aPinned ? -1 : 1;
          }

          const lengthComparison = a.allUniques.length - b.allUniques.length;
          if (!this.settings.alwaysGroupByBaseType() && lengthComparison !== 0) {
            return lengthComparison;
          }
          return (b.allUniques[0].price || 0) - (a.allUniques[0].price || 0) || a.baseType.localeCompare(b.baseType);
        });
      });
    });

    this.tierList = [
      { tiers: [tiers.find(t => t.tier === 'Excellent')!] },
      { tiers: [tiers.find(t => t.tier === 'Good')!] },
      { tiers: [tiers.find(t => t.tier === 'Class')!, tiers.find(t => t.tier === 'Potential')!] },
      { tiers: [tiers.find(t => t.tier === 'Restricted')!] },
      { tiers: [tiers.find(t => t.tier === 'Other')!] },
    ]

    this.updateFilteredOtherBaseTypes();
  }

  private applyChangedOnly(t: TierDataVm): TierDataVm {
    if (!this.changedOnly) return t;
    return {
      ...t,
      classes: t.classes
        .map(g => ({
          ...g,
          items: g.items.filter(item => hasMovedFromOriginalTier(item.originalTier, t.tier)),
        }))
        .filter(g => g.items.length > 0),
    };
  }

  updateFilteredOtherBaseTypes() {
    if (this.settings.showAllOtherUniques()) {
      // When showAllOther is true, show all items in the Other tier
      this.filteredOtherBaseTypes = this.tierList.flatMap(t => t.tiers).find(t => t.tier === 'Other')!.classes;
      return;
    }

    if (!this.uniqueFilter || this.uniqueFilter.length < 2) {
      this.filteredOtherBaseTypes = this.tierList.flatMap(t => t.tiers).find(t => t.tier === 'Other')!.classes
        .map(classGroup => ({
          ...classGroup,
          items: classGroup.items.filter(item => hasMovedFromOriginalTier(item.originalTier, 'Other'))
        }))
        .filter(classGroup => classGroup.items.length > 0);
      return;
    }

    this.filteredOtherBaseTypes = this.tierList.flatMap(t => t.tiers).find(t => t.tier === 'Other')!.classes
      .map(classGroup => ({
        ...classGroup,
        items: classGroup.items.filter(item =>
          this.matchesUniqueFilter(item)
          || hasMovedFromOriginalTier(item.originalTier, 'Other')
        )
      }))
      .filter(classGroup => classGroup.items.length > 0)
  }

  updateFilter() {
    this.filterService.updateFilter();
  }

  onDragStart(event: DragEvent, baseType: TieredUniqueVm) {
    this.draggedItem = baseType;
    event.dataTransfer?.setData('baseType', JSON.stringify(baseType));
  }

  onDragEnd() {
    this.draggedItem = null;
  }

  onDragEnter(event: DragEvent, tier: TierDataVm) {
    event.preventDefault();
    tier.dragCounter = (tier.dragCounter || 0) + 1;
    tier.isDragOver = true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onDragLeave(event: DragEvent, tier: TierDataVm) {
    tier.dragCounter = (tier.dragCounter || 0) - 1;
    if (tier.dragCounter === 0) {
      tier.isDragOver = false;
    }
  }

  onDrop(event: DragEvent, targetTier: TierDataVm) {
    event.preventDefault();
    targetTier.dragCounter = 0;
    targetTier.isDragOver = false;
    const baseTypeJson = event.dataTransfer!.getData('baseType');
    if (!baseTypeJson) return;
    const baseType = JSON.parse(baseTypeJson) as TieredUniqueVm;
    this.moveItemToTier(baseType, targetTier.tier || targetTier.name);
    this.draggedItem = null;
  }

  resetToOriginalTier(baseType: TieredUniqueVm) {
    this.filterService.resetUniqueItemTier(baseType.baseType);
    this.tieredUniques = this.uniquesTierService.getTieredUniques();
    this.buildTierList();
  }

  rebuildTierList() {
    this.uniquesTierService.combineTieredUniques(this.filter);
    this.chanceBasesTierService.combineTieredChanceBases(this.filter);
    this.tieredUniques = this.uniquesTierService.getTieredUniques();
    this.buildTierList();
    this.updateFilter();
  }

  moveItemToTier(baseType: TieredUniqueVm, targetTier: UniqueTierType) {
    if (baseType.tier === targetTier) return;
    this.filterService.updateUniqueItemTier(baseType.baseType, targetTier);
    this.tieredUniques = this.uniquesTierService.getTieredUniques();
    this.buildTierList();
  }

  hideInfoMessage() {
    this.settings.dismissUniquesTierListMessage();
  }

  toggleAlwaysGroupByBaseType() {
    this.settings.toggleAlwaysGroupByBaseType();
    this.buildTierList();
  }

  toggleShowAllOther() {
    this.settings.toggleShowAllOtherUniques();
    this.buildTierList();
  }

  selectTieringMode(mode: UniquesTieringMode) {
    this.uniquesTierService.setTieringMode(mode, this.filter);
    this.buildTierList();
    this.updateFilter();
  }

  openCustomTieringDialog() {
    this.showCustomTieringDialog = true;
  }

  closeCustomTieringDialog() {
    this.showCustomTieringDialog = false;
    this.buildTierList();
    this.updateFilter();
  }

  disableHideAllRemainingWhenAllUniquesAreEnabled = () => {
    if (this.filter.uniqueTierCosmetics[0].displayOptions.active
      && this.filter.uniqueTierCosmetics[1].displayOptions.active
      && this.filter.uniqueTierCosmetics[2].displayOptions.active
      && this.filter.uniqueTierCosmetics[3].displayOptions.active
      && this.filter.uniqueTierCosmetics[4].displayOptions.active) {
      this.filter.hideAllRemainingUniques = false;
    }
  }

  private matchesUniqueFilter(item: TieredUnique): boolean {
    const filter = this.uniqueFilter.toLowerCase();
    return item.baseType.toLowerCase().includes(filter)
      || item.allUniques.some(unique =>
        unique.name.toLowerCase().includes(filter)
        || unique.bossDrop?.toLowerCase().includes(filter)
        || unique.bossDropContent?.toLowerCase().includes(filter)
      );
  }

  private getTierColor(tier: UniqueTierType | undefined): string {
    if (!tier) return 'visible';
    const tierCosmetic = this.filter.uniqueTierCosmetics.find(t => t.uniqueTier === tier);
    return (tierCosmetic?.displayOptions.active && tierCosmetic?.displayOptions.cosmeticOptions?.highlight && tierCosmetic?.displayOptions.cosmeticOptions?.highlightColor)
      ? tierCosmetic.displayOptions.cosmeticOptions.highlightColor.toLocaleLowerCase()
      : 'visible';
  }

  private buildFavouriteContentDisplayByBaseType(
    favouriteRestrictedBaseTypes: ReadonlySet<string>,
    favouriteLowValueBaseTypes: ReadonlySet<string>,
    bossOnlyLowValueBossUniques: BossOnlyLowValueBossUnique[],
  ): Map<string, { label: string; icon: string }> {
    const displayByBaseType = new Map<string, { label: string; icon: string }>();

    const addRestrictedDisplay = (item: TieredUnique) => {
      if (displayByBaseType.has(item.baseType)) return;

      const unique = this.endgameContentPresetService.getFavouriteContentDisplayUnique(this.filter, item);
      if (!unique?.bossDropContent) return;

      displayByBaseType.set(item.baseType, {
        label: EndgameContentLabelMap[unique.bossDropContent],
        icon: EndgameContentIconMap[unique.bossDropContent],
      });
    };

    const addLowValueDisplay = (item: TieredUnique, bossDropContent?: EndgameContent) => {
      if (displayByBaseType.has(item.baseType)) return;

      const content = bossDropContent ?? item.allUniques.find(u => u.bossDropContent)?.bossDropContent;
      if (!content) return;

      displayByBaseType.set(item.baseType, {
        label: EndgameContentLabelMap[content],
        icon: EndgameContentIconMap[content],
      });
    };

    if (favouriteRestrictedBaseTypes.size > 0) {
      for (const item of this.tieredUniques['Restricted']) {
        if (favouriteRestrictedBaseTypes.has(item.baseType)) {
          addRestrictedDisplay(item);
        }
      }
    }

    if (favouriteLowValueBaseTypes.size > 0) {
      const bossOnlyByBaseType = new Map(bossOnlyLowValueBossUniques.map(x => [x.baseType, x]));
      for (const item of this.tieredUniques['Other']) {
        if (favouriteLowValueBaseTypes.has(item.baseType)) {
          addLowValueDisplay(item, bossOnlyByBaseType.get(item.baseType)?.bossDropContent);
        }
      }
    }

    return displayByBaseType;
  }

  private computeItemFilterVmState(
    baseType: string,
    tier: UniqueTierType,
    tierActive: boolean,
    ctx: UniqueItemFilterContext,
  ): Pick<TieredUniqueVm, 'inactiveByFilter' | 'pinnedFavouriteContentLabel' | 'pinnedFavouriteContentIcon' | 'pinnedOtherLowValueBossShown'> {
    const favouriteDisplay = ctx.favouriteContentDisplayByBaseType.get(baseType);

    if (!tierActive) {
      if (tier === 'Restricted' && ctx.favouriteRestrictedBaseTypes.has(baseType)) {
        return {
          inactiveByFilter: false,
          pinnedFavouriteContentLabel: favouriteDisplay?.label,
          pinnedFavouriteContentIcon: favouriteDisplay?.icon,
        };
      }
      if (tier === 'Other' && ctx.favouriteLowValueBaseTypes.has(baseType)) {
        return {
          inactiveByFilter: false,
          pinnedFavouriteContentLabel: favouriteDisplay?.label,
          pinnedFavouriteContentIcon: favouriteDisplay?.icon,
        };
      }
      if (tier === 'Other' && ctx.otherLowValueBaseTypes.has(baseType)) {
        return {
          inactiveByFilter: false,
          pinnedOtherLowValueBossShown: true,
        };
      }
      return { inactiveByFilter: true };
    }

    if (tier === 'Restricted' && ctx.dimNonFavouriteRestricted && !ctx.favouriteRestrictedBaseTypes.has(baseType)) {
      return { inactiveByFilter: true };
    }

    if (tier === 'Other' && ctx.dimNonExceptionLowValue
      && !ctx.favouriteLowValueBaseTypes.has(baseType)
      && !ctx.otherLowValueBaseTypes.has(baseType)) {
      return { inactiveByFilter: true };
    }

    return {
      inactiveByFilter: false,
      pinnedFavouriteContentLabel: favouriteDisplay?.label,
      pinnedFavouriteContentIcon: favouriteDisplay?.icon,
    };
  }
}
